import type { ReactElement } from "react";
import { Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../utils/dateService";

/**
 * Display helpers for casino scheduled-task runs, shared between the
 * Scheduled Tasks admin page (CASENG-1048) and the games-catalog scheduler
 * menu (CASENG-1058) so neither duplicates the status/color mapping or the
 * msg_code → copy table.
 */

const EMPTY_DISPLAY = "—";
const ERROR_EXCERPT_MAX = 80;

export type ScheduledTaskRun = {
  run_id: string;
  purpose: string;
  status: string;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  result?: string | null;
  result_truncated?: boolean;
  error?: string | null;
  error_truncated?: boolean;
  correlation_id?: string | null;
};

// AppStrings keys (values are read at render time — TCM overrides the
// defaults after module load, so no `AppStrings.*` at module scope).
const PURPOSE_LABEL_KEYS: Record<string, string> = {
  "generate-betcore-access-token":
    "CASINO_SCHEDULED_TASKS_PURPOSE_GENERATE_BETCORE_ACCESS_TOKEN",
  "load-casino-games": "CASINO_SCHEDULED_TASKS_PURPOSE_LOAD_CASINO_GAMES",
  "overwrite-casino-game-images":
    "CASINO_SCHEDULED_TASKS_PURPOSE_OVERWRITE_CASINO_GAME_IMAGES",
  "overwrite-casino-game-images-force-refresh":
    "CASINO_SCHEDULED_TASKS_PURPOSE_OVERWRITE_CASINO_GAME_IMAGES_FORCE_REFRESH",
  "create-default-casino-entities":
    "CASINO_SCHEDULED_TASKS_PURPOSE_CREATE_DEFAULT_CASINO_ENTITIES",
};

const STATUS_META: Record<string, { color: string; labelKey: string }> = {
  pending: { color: "gold", labelKey: "CASINO_SCHEDULED_TASKS_STATUS_PENDING" },
  running: {
    color: "processing",
    labelKey: "CASINO_SCHEDULED_TASKS_STATUS_RUNNING",
  },
  success: {
    color: "success",
    labelKey: "CASINO_SCHEDULED_TASKS_STATUS_SUCCESS",
  },
  failed: { color: "error", labelKey: "CASINO_SCHEDULED_TASKS_STATUS_FAILED" },
  orphaned: {
    color: "warning",
    labelKey: "CASINO_SCHEDULED_TASKS_STATUS_ORPHANED",
  },
};

export function purposeLabel(purpose: string): string {
  const key = PURPOSE_LABEL_KEYS[purpose];
  return (key && AppStrings[key]) || purpose;
}

export function statusTag(status?: string | null): ReactElement {
  const meta = STATUS_META[status ?? ""];
  return (
    <Tag color={meta?.color ?? "default"}>
      {meta ? AppStrings[meta.labelKey] : (status ?? EMPTY_DISPLAY)}
    </Tag>
  );
}

export function formatRunDate(iso?: string | null): string {
  return iso ? convertToUserTimezone(iso, "YYYY-MM-DD HH:mm:ss") : EMPTY_DISPLAY;
}

export function errorExcerpt(text?: string | null): string {
  if (!text) {
    return EMPTY_DISPLAY;
  }
  return text.length > ERROR_EXCERPT_MAX
    ? `${text.slice(0, ERROR_EXCERPT_MAX)}…`
    : text;
}

/** Friendly copy for known trigger failures, keyed on `msg_code` (never prose). */
export function triggerErrorCopy(error: unknown): string {
  const msgCode = (
    error as { response?: { data?: { msg_code?: string } } } | null
  )?.response?.data?.msg_code;
  switch (msgCode) {
    case "casino.error.scheduled_task_purpose_not_allowed":
      return AppStrings.CASINO_SCHEDULED_TASKS_PURPOSE_NOT_ALLOWED;
    case "casino.error.scheduled_task_consumer_disabled":
      return AppStrings.CASINO_SCHEDULED_TASKS_CONSUMER_DISABLED;
    case "casino.error.scheduled_task_queue_unavailable":
      return AppStrings.CASINO_SCHEDULED_TASKS_QUEUE_UNAVAILABLE;
    default:
      return AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER_ERROR;
  }
}

export function getScheduledTaskColumns(): ColumnsType<ScheduledTaskRun> {
  return [
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_STATUS,
      dataIndex: "status",
      key: "status",
      render: (status: string) => statusTag(status),
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_CREATED,
      dataIndex: "created_at",
      key: "created_at",
      render: formatRunDate,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_STARTED,
      dataIndex: "started_at",
      key: "started_at",
      render: formatRunDate,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_FINISHED,
      dataIndex: "finished_at",
      key: "finished_at",
      render: formatRunDate,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_CORRELATION_ID,
      dataIndex: "correlation_id",
      key: "correlation_id",
      render: (id?: string | null) => id ?? EMPTY_DISPLAY,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_ERROR,
      dataIndex: "error",
      key: "error",
      render: (error?: string | null) => (
        <span title={error ?? undefined}>{errorExcerpt(error)}</span>
      ),
    },
  ];
}
